// DM message bubbles (voice + text) extracted from DMView.js.
// Kept purposely small and stateless-ish: each bubble owns only its
// audio-decrypt lifecycle. Higher-level state (conversation list,
// scroll, unread counters) stays in DMView.

import React, { useCallback, useRef, useState } from "react";
import { Loader2, MoreVertical, Pause, Play, Trash2 } from "lucide-react";
import { decryptBytesFromPeer } from "../lib/dmCrypto";

const API = process.env.REACT_APP_BACKEND_URL || "";

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  return sameDay
    ? d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
}

function fmtSecs(ms) {
  const s = Math.max(0, Math.floor((ms || 0) / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}


function expiryLabel(expiresAt) {
  if (!expiresAt) return "";
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return "in scadenza";
  const mins = Math.ceil(ms / 60000);
  if (mins < 60) return `scompare tra ${mins} min`;
  const hours = Math.ceil(mins / 60);
  if (hours < 48) return `scompare tra ${hours} h`;
  return `scompare tra ${Math.ceil(hours / 24)} g`;
}

function MessageActions({ msg, mine, onDelete }) {
  const [open, setOpen] = useState(false);
  if (!onDelete) return null;
  return (
    <div className="relative self-start">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-6 h-6 rounded-full flex items-center justify-center opacity-60 hover:opacity-100"
        aria-label="Azioni messaggio"
        data-testid={`dm-message-menu-${msg.id}`}
      >
        <MoreVertical size={13} />
      </button>
      {open && (
        <div className="absolute right-0 top-7 z-20 w-44 rounded-xl border border-edge bg-white text-ink shadow-lg p-1">
          <button
            type="button"
            onClick={() => { setOpen(false); onDelete(msg, "me"); }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-cream text-xs text-left"
          >
            <Trash2 size={13} /> Cancella per me
          </button>
          {mine && (
            <button
              type="button"
              onClick={() => { setOpen(false); onDelete(msg, "everyone"); }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-primary/5 text-primary text-xs text-left"
            >
              <Trash2 size={13} /> Cancella per tutti
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function renderPlayIcon(loading, playing) {
  if (loading) return <Loader2 size={16} className="animate-spin" />;
  if (playing) return <Pause size={16} />;
  return <Play size={16} />;
}

export function VoiceMessageBubble({ msg, mine, peerX25519, onDelete }) {
  const [state, setState] = useState({ url: null, loading: false, error: null });
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const decrypt = useCallback(async () => {
    if (state.url || state.loading || !peerX25519) return;
    setState({ url: null, loading: true, error: null });
    try {
      const res = await fetch(`${API}/api/v1/uploads/audio/${encodeURIComponent(msg.audioKey)}`);
      if (!res.ok) throw new Error("Audio non trovato");
      const encBytes = new Uint8Array(await res.arrayBuffer());
      const plain = await decryptBytesFromPeer(peerX25519, encBytes, msg.audioIv);
      if (!plain) throw new Error("Impossibile decifrare l'audio");
      const blob = new Blob([plain], { type: "audio/webm" });
      setState({ url: URL.createObjectURL(blob), loading: false, error: null });
    } catch (e) {
      setState({ url: null, loading: false, error: e?.message || "errore" });
    }
  }, [msg.audioKey, msg.audioIv, peerX25519, state.url, state.loading]);

  const toggle = async () => {
    if (!state.url) {
      await decrypt();
      return; // user taps again to play once decrypted
    }
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) { void el.play(); setPlaying(true); }
    else { el.pause(); setPlaying(false); }
  };

  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`dm-msg-${msg.id}`}>
      <div className={`max-w-[75%] rounded-2xl px-3 py-2 shadow-sm ${mine ? "bg-ink text-white rounded-br-sm" : "bg-white border border-edge text-ink rounded-bl-sm"}`}>
        <div className="flex items-center gap-2">
          <button
            onClick={toggle}
            disabled={state.loading}
            className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${mine ? "bg-white/20 hover:bg-white/30 text-white" : "bg-ink text-white hover:bg-black"}`}
            aria-label={playing ? "Pausa" : "Play"}
            data-testid={`dm-voice-toggle-${msg.id}`}
          >
            {renderPlayIcon(state.loading, playing)}
          </button>
          <div className="min-w-0">
            <div className={`text-[11px] uppercase tracking-widest font-semibold ${mine ? "text-white/70" : "text-muted"}`}>
              Voce · {fmtSecs(msg.durationMs)}
            </div>
            {state.error && <div className="text-[10px] text-primary">{state.error}</div>}
          </div>
          <MessageActions msg={msg} mine={mine} onDelete={onDelete} />
        </div>
        {state.url && (
          <audio
            ref={audioRef}
            src={state.url}
            onEnded={() => setPlaying(false)}
            className="hidden"
          />
        )}
        <div className={`mt-1 text-[10px] tabular-nums ${mine ? "text-white/60" : "text-muted"}`}>
          {formatTime(msg.sentAt)}{msg.expiresAt ? ` · ${expiryLabel(msg.expiresAt)}` : ""}
        </div>
      </div>
    </div>
  );
}


export function MediaMessageBubble({
  msg,
  mine,
  peerX25519,
  onDelete,
}) {
  const [state, setState] = useState({
    url: null,
    loading: false,
    error: null,
  });

  const decrypt = useCallback(async () => {
    if (
      state.url ||
      state.loading ||
      !peerX25519 ||
      !msg.attachmentKey ||
      !msg.attachmentIv
    ) {
      return;
    }

    setState({
      url: null,
      loading: true,
      error: null,
    });

    try {
      // Lo storage è lo stesso deposito opaco usato dai vocali.
      const res = await fetch(
        `${API}/api/v1/uploads/audio/${encodeURIComponent(
          msg.attachmentKey
        )}`
      );

      if (!res.ok) {
        throw new Error("Allegato non trovato");
      }

      const encBytes = new Uint8Array(
        await res.arrayBuffer()
      );

      const plain = await decryptBytesFromPeer(
        peerX25519,
        encBytes,
        msg.attachmentIv
      );

      if (!plain) {
        throw new Error(
          "Impossibile decifrare l'allegato"
        );
      }

      const fallbackType =
        msg.kind === "video"
          ? "video/mp4"
          : "image/jpeg";

      const blob = new Blob(
        [plain],
        {
          type: msg.mimeType || fallbackType,
        }
      );

      setState({
        url: URL.createObjectURL(blob),
        loading: false,
        error: null,
      });
    } catch (e) {
      setState({
        url: null,
        loading: false,
        error:
          e?.message ||
          "Errore nella decifratura dell'allegato",
      });
    }
  }, [
    msg.attachmentKey,
    msg.attachmentIv,
    msg.mimeType,
    msg.kind,
    peerX25519,
    state.url,
    state.loading,
  ]);

  return (
    <div
      className={`flex ${
        mine ? "justify-end" : "justify-start"
      }`}
      data-testid={`dm-msg-${msg.id}`}
    >
      <div
        className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
          mine
            ? "bg-ink text-white rounded-br-sm"
            : "bg-white border border-edge text-ink rounded-bl-sm"
        }`}
      >
        {!state.url && (
          <button
            type="button"
            onClick={decrypt}
            disabled={state.loading}
            className={`rounded-xl px-3 py-2 text-xs font-semibold ${
              mine
                ? "bg-white/10 text-white"
                : "bg-cream text-ink"
            } disabled:opacity-50`}
            data-testid={`dm-media-open-${msg.id}`}
          >
            {state.loading
              ? "Decifratura..."
              : msg.kind === "video"
              ? "Apri video cifrato"
              : "Apri immagine cifrata"}
          </button>
        )}

        {state.error && (
          <div className="text-xs text-primary">
            {state.error}
          </div>
        )}

        {state.url && msg.kind === "image" && (
          <img
            src={state.url}
            alt={msg.fileName || "Immagine privata"}
            className="max-h-80 max-w-full rounded-xl object-contain"
          />
        )}

        {state.url && msg.kind === "video" && (
          <video
            src={state.url}
            controls
            playsInline
            className="max-h-96 max-w-full rounded-xl"
          />
        )}

        {msg.fileName && (
          <div
            className={`mt-1 text-[10px] ${
              mine
                ? "text-white/60"
                : "text-muted"
            }`}
          >
            {msg.fileName}
          </div>
        )}

        <MessageActions
          msg={msg}
          mine={mine}
          onDelete={onDelete}
        />

        <div
          className={`mt-1 text-[10px] tabular-nums ${
            mine
              ? "text-white/60"
              : "text-muted"
          }`}
        >
          {formatTime(msg.sentAt)}
          {msg.expiresAt
            ? ` · ${expiryLabel(msg.expiresAt)}`
            : ""}
        </div>
      </div>
    </div>
  );
}


export function TextMessageBubble({ msg, mine, onDelete }) {
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`dm-msg-${msg.id}`}>
      <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm shadow-sm ${mine ? "bg-ink text-white rounded-br-sm" : "bg-white border border-edge text-ink rounded-bl-sm"}`}>
        <div className="flex items-start gap-1">
          <div className="whitespace-pre-wrap break-words flex-1">{msg.plaintext ?? "…"}</div>
          <MessageActions msg={msg} mine={mine} onDelete={onDelete} />
        </div>
        <div className={`mt-1 text-[10px] tabular-nums ${mine ? "text-white/60" : "text-muted"}`}>
          {formatTime(msg.sentAt)}{msg.expiresAt ? ` · ${expiryLabel(msg.expiresAt)}` : ""}
        </div>
      </div>
    </div>
  );
}
